<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import ContactAPI from 'dashboard/api/contacts';
import BulkActionsAPI from 'dashboard/api/bulkActions';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  selectedContactIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['applied']);
const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);
const isLoading = ref(false);

// Step management
const currentStep = ref(1);

// Step 1: Labels
const labelQuery = ref('');
const selectedLabels = ref([]);

// Step 2: Contacts
const contactScope = ref('all');
const contactSearch = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const pickedContactIds = ref([]);
const searchPage = ref(1);
const searchTotal = ref(0);

const labels = useMapGetter('labels/getLabels');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const totalContacts = computed(() => meta.value?.count || 0);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);

const filteredLabels = computed(() => {
  if (!labelQuery.value) return labels.value;
  return labels.value.filter(label =>
    label.title.toLowerCase().includes(labelQuery.value.toLowerCase())
  );
});

const hasSelectedLabels = computed(() => selectedLabels.value.length > 0);

const hasMoreResults = computed(
  () => searchResults.value.length < searchTotal.value
);

const stepTitle = computed(() =>
  currentStep.value === 1
    ? t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.STEP1_TITLE')
    : t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.STEP2_TITLE')
);

const stepDescription = computed(() =>
  currentStep.value === 1
    ? t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.STEP1_DESC')
    : t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.STEP2_DESC')
);

const canProceedToStep2 = computed(() => hasSelectedLabels.value);

const canSubmit = computed(() => {
  if (!hasSelectedLabels.value) return false;
  if (contactScope.value === 'specific' && pickedContactIds.value.length === 0)
    return false;
  return true;
});

const resetState = () => {
  currentStep.value = 1;
  selectedLabels.value = [];
  labelQuery.value = '';
  contactScope.value = 'all';
  contactSearch.value = '';
  searchResults.value = [];
  pickedContactIds.value = [];
  searchPage.value = 1;
  searchTotal.value = 0;
};

const fetchContacts = async (searchTerm = '', page = 1) => {
  isSearching.value = true;
  try {
    const response = searchTerm
      ? await ContactAPI.search(searchTerm, page, 'name')
      : await ContactAPI.get(page, 'name');

    const payload = response.data?.payload || [];
    const apiMeta = response.data?.meta || {};

    if (page === 1) {
      searchResults.value = payload;
    } else {
      searchResults.value = [...searchResults.value, ...payload];
    }
    searchTotal.value = apiMeta.count || payload.length;
    searchPage.value = page;
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = debounce(value => {
  fetchContacts(value, 1);
}, 300);

const loadMore = () => {
  if (hasMoreResults.value && !isSearching.value) {
    fetchContacts(contactSearch.value, searchPage.value + 1);
  }
};

const toggleContact = id => {
  const idx = pickedContactIds.value.indexOf(id);
  if (idx >= 0) {
    pickedContactIds.value = pickedContactIds.value.filter(cid => cid !== id);
  } else {
    pickedContactIds.value = [...pickedContactIds.value, id];
  }
};

const goToStep2 = () => {
  if (!canProceedToStep2.value) return;
  currentStep.value = 2;
  // Pre-populate picked contacts from the ones already selected in the list
  if (
    props.selectedContactIds.length > 0 &&
    pickedContactIds.value.length === 0
  ) {
    pickedContactIds.value = [...props.selectedContactIds];
    contactScope.value = 'specific';
  }
  if (contactScope.value === 'specific' && searchResults.value.length === 0) {
    fetchContacts('', 1);
  }
};

const goBackToStep1 = () => {
  currentStep.value = 1;
};

watch(contactSearch, val => {
  debouncedSearch(val);
});

watch(contactScope, val => {
  if (val === 'specific' && searchResults.value.length === 0) {
    fetchContacts('', 1);
  }
});

const handleSubmit = async () => {
  if (!canSubmit.value) return;

  isLoading.value = true;
  try {
    if (contactScope.value === 'specific') {
      await BulkActionsAPI.create({
        type: 'Contact',
        ids: pickedContactIds.value,
        labels: { add: selectedLabels.value },
      });
    } else {
      let payload = { labels: selectedLabels.value };

      if (activeSegmentId.value && activeSegment.value) {
        payload = { ...payload, ...activeSegment.value.query };
      } else if (Object.keys(appliedFilters.value).length > 0) {
        payload = {
          ...payload,
          ...filterQueryGenerator(appliedFilters.value),
        };
      }

      const activeLabel = route.params.label;
      if (activeLabel) {
        payload.label = activeLabel;
      }

      await ContactAPI.bulkAssignLabels(payload);
    }

    useAlert(t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
    resetState();
    emit('applied');
  } catch {
    useAlert(t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="stepTitle"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="xl"
    @close="resetState"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ stepDescription }}
      </p>
    </template>

    <!-- Step 1: Select labels -->
    <div v-if="currentStep === 1" class="flex flex-col gap-3">
      <div
        class="flex items-center h-8 gap-2 px-2.5 border border-solid rounded-lg border-n-strong bg-n-alpha-black2"
      >
        <span class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0" />
        <input
          v-model="labelQuery"
          type="search"
          :placeholder="
            t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SEARCH_LABELS')
          "
          class="reset-base w-full text-sm border-0 outline-0 bg-transparent dark:bg-transparent h-full"
        />
      </div>
      <ul
        class="flex flex-col gap-0.5 max-h-60 overflow-y-auto m-0 list-none p-0"
      >
        <li v-for="label in filteredLabels" :key="label.id">
          <label
            class="flex items-center gap-2.5 py-1 px-2.5 rounded-md cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
            :class="{
              'bg-n-slate-2 dark:bg-n-solid-2': selectedLabels.includes(
                label.title
              ),
            }"
          >
            <input
              v-model="selectedLabels"
              type="checkbox"
              :value="label.title"
              class="m-0"
            />
            <span class="text-sm truncate flex-grow">{{ label.title }}</span>
            <span
              class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
              :style="{ backgroundColor: label.color }"
            />
          </label>
        </li>
        <li
          v-if="filteredLabels.length === 0"
          class="text-sm text-n-slate-10 px-2.5 py-2"
        >
          {{ t('CONTACTS_BULK_ACTIONS.NO_LABELS_FOUND') }}
        </li>
      </ul>
      <div v-if="selectedLabels.length > 0" class="flex flex-wrap gap-1.5 pt-1">
        <span
          v-for="lbl in selectedLabels"
          :key="lbl"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-n-slate-3 dark:bg-n-solid-3 text-n-slate-12"
        >
          {{ lbl }}
          <button
            type="button"
            class="i-lucide-x size-3 cursor-pointer text-n-slate-10 hover:text-n-slate-12"
            @click="
              selectedLabels = selectedLabels.filter(item => item !== lbl)
            "
          />
        </span>
      </div>
    </div>

    <!-- Step 2: Select contacts -->
    <div v-if="currentStep === 2" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label
          class="flex items-center gap-2 py-1.5 px-2.5 rounded-md cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
          :class="{ 'bg-n-slate-2 dark:bg-n-solid-2': contactScope === 'all' }"
        >
          <input v-model="contactScope" type="radio" value="all" class="m-0" />
          <span class="text-sm">
            {{
              t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SCOPE_ALL', {
                count: totalContacts,
              })
            }}
          </span>
        </label>
        <label
          class="flex items-center gap-2 py-1.5 px-2.5 rounded-md cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
          :class="{
            'bg-n-slate-2 dark:bg-n-solid-2': contactScope === 'specific',
          }"
        >
          <input
            v-model="contactScope"
            type="radio"
            value="specific"
            class="m-0"
          />
          <span class="text-sm">
            {{ t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SCOPE_SPECIFIC') }}
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span
              v-if="pickedContactIds.length > 0"
              class="text-xs text-n-slate-10"
            >
              ({{ pickedContactIds.length }}
              {{
                t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SELECTED_SUFFIX')
              }})
            </span>
          </span>
        </label>
      </div>

      <!-- Contact picker (only when specific is selected) -->
      <template v-if="contactScope === 'specific'">
        <div
          class="flex items-center h-8 gap-2 px-2.5 border border-solid rounded-lg border-n-strong bg-n-alpha-black2"
        >
          <span class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0" />
          <input
            v-model="contactSearch"
            type="search"
            :placeholder="
              t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.SEARCH_CONTACTS')
            "
            class="reset-base w-full text-sm border-0 outline-0 bg-transparent dark:bg-transparent h-full"
          />
        </div>

        <div
          v-if="isSearching && searchResults.length === 0"
          class="flex items-center justify-center py-4"
        >
          <Spinner />
        </div>

        <ul
          v-else
          class="flex flex-col gap-0.5 max-h-52 overflow-y-auto m-0 list-none p-0"
        >
          <li v-for="contact in searchResults" :key="contact.id">
            <label
              class="flex items-center gap-2.5 py-1.5 px-2.5 rounded-md cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
              :class="{
                'bg-n-slate-2 dark:bg-n-solid-2': pickedContactIds.includes(
                  contact.id
                ),
              }"
            >
              <input
                type="checkbox"
                :checked="pickedContactIds.includes(contact.id)"
                class="m-0"
                @change="toggleContact(contact.id)"
              />
              <img
                v-if="contact.thumbnail"
                :src="contact.thumbnail"
                class="w-6 h-6 rounded-full object-cover flex-shrink-0"
              />
              <span
                v-else
                class="flex items-center justify-center w-6 h-6 rounded-full bg-n-slate-3 dark:bg-n-solid-3 text-xs font-medium text-n-slate-11 flex-shrink-0"
              >
                {{ (contact.name || '?').charAt(0).toUpperCase() }}
              </span>
              <div class="flex flex-col min-w-0 flex-grow">
                <span class="text-sm truncate">{{ contact.name }}</span>
                <span
                  v-if="contact.phone_number || contact.email"
                  class="text-xs text-n-slate-10 truncate"
                >
                  {{ contact.phone_number || contact.email }}
                </span>
              </div>
            </label>
          </li>
          <li
            v-if="searchResults.length === 0 && !isSearching"
            class="text-sm text-n-slate-10 px-2.5 py-2"
          >
            {{
              t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.NO_CONTACTS_FOUND')
            }}
          </li>
        </ul>

        <button
          v-if="hasMoreResults"
          type="button"
          class="flex items-center justify-center gap-1.5 py-1.5 text-sm text-n-brand cursor-pointer hover:underline bg-transparent border-0"
          :disabled="isSearching"
          @click="loadMore"
        >
          <Spinner v-if="isSearching" class="size-3" />
          {{ t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.LOAD_MORE') }}
        </button>

        <!-- Selected contacts chips -->
        <div
          v-if="pickedContactIds.length > 0"
          class="text-xs text-n-slate-10 pt-1"
        >
          {{ pickedContactIds.length }}
          {{ t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.CONTACTS_SELECTED') }}
        </div>
      </template>
    </div>

    <!-- Custom footer with navigation -->
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <!-- Step indicator -->
        <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <div class="flex items-center gap-1.5">
          <span
            class="flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium"
            :class="
              currentStep === 1
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 dark:bg-n-solid-3 text-n-slate-11'
            "
          >
            1
          </span>
          <span class="w-4 h-px bg-n-slate-5" />
          <span
            class="flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium"
            :class="
              currentStep === 2
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 dark:bg-n-solid-3 text-n-slate-11'
            "
          >
            2
          </span>
        </div>
        <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

        <div class="flex items-center gap-2">
          <Button
            v-if="currentStep === 2"
            variant="faded"
            color="slate"
            :label="t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.BACK')"
            type="button"
            @click="goBackToStep1"
          />
          <Button
            v-if="currentStep === 1"
            variant="faded"
            color="slate"
            :label="t('DIALOG.BUTTONS.CANCEL')"
            type="button"
            @click="dialogRef?.close()"
          />
          <Button
            v-if="currentStep === 1"
            color="blue"
            :label="t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.NEXT')"
            :disabled="!canProceedToStep2"
            type="button"
            @click="goToStep2"
          />
          <Button
            v-if="currentStep === 2"
            color="blue"
            :label="t('CONTACTS_BULK_ACTIONS.BULK_LABELS_DIALOG.APPLY_BUTTON')"
            :disabled="!canSubmit || isLoading"
            :is-loading="isLoading"
            type="button"
            @click="handleSubmit"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
